package com.example.mystybag.cropImage.expected

import com.example.mystybag.sharedmodels.SCImage
import kotlinx.browser.document
import kotlinx.coroutines.suspendCancellableCoroutine
import kotlin.coroutines.resume
import kotlin.coroutines.resumeWithException
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLImageElement

private fun ByteArray.toBase64DataUrl(mime: String = "image/png"): String {
    val binary = buildString { this@toBase64DataUrl.forEach { append(it.toInt().and(0xFF).toChar()) } }
    val base64 = js("btoa").unsafeCast<(String) -> String>()(binary)
    return "data:$mime;base64,$base64"
}

private fun dataUrlToByteArray(dataUrl: String): ByteArray {
    val base64 = dataUrl.substringAfter(',')
    val binary = js("atob").unsafeCast<(String) -> String>()(base64)
    val charCodeAt = js("(function(s,i){return s.charCodeAt(i);})").unsafeCast<(String, Int) -> Int>()
    return ByteArray(binary.length) { i -> charCodeAt(binary, i).and(0xFF).toByte() }
}

private suspend fun loadImage(dataUrl: String): HTMLImageElement = suspendCancellableCoroutine { cont ->
    val img = (document.createElement("img") as HTMLImageElement).apply {
        onload = {
            cont.resume(this@apply)
        }
        onerror = { _: dynamic, _: String?, _: Int, _: Int, _: Any? ->
            cont.resumeWithException(Throwable("Failed to load image"))
            null
        }
        src = dataUrl
    }
    cont.invokeOnCancellation {
        img.src = ""
    }
}

/** Rotate image 90° clockwise (matches Android 270°). */
actual suspend fun rotateImage(image: SCImage): SCImage? {
    return try {
        val dataUrl = image.data.toBase64DataUrl()
        val img = loadImage(dataUrl)
        val w = img.naturalWidth
        val h = img.naturalHeight
        val canvas = document.createElement("canvas") as HTMLCanvasElement
        canvas.width = h
        canvas.height = w
        val ctx = canvas.getContext("2d")?.unsafeCast<dynamic>() ?: return null
        ctx.translate(h.toDouble(), 0.0)
        ctx.rotate(90.0 * kotlin.math.PI / 180.0)
        ctx.drawImage(img, 0.0, 0.0)
        val outDataUrl = canvas.toDataURL("image/png")
        SCImage(dataUrlToByteArray(outDataUrl))
    } catch (_: Throwable) {
        null
    }
}

actual suspend fun cropImage(
    image: SCImage,
    newWidth: Int,
    newHeight: Int,
    xPosition: Int,
    yPosition: Int,
    width: Int,
    height: Int,
    zoom: Float,
    outputWidth: Int?,
    outputHeight: Int?,
): SCImage? {
    return try {
        val dataUrl = image.data.toBase64DataUrl()
        val img = loadImage(dataUrl)
        val origW = img.naturalWidth
        val origH = img.naturalHeight
        val startX = (xPosition.toDouble() / newWidth) * origW
        val startY = (yPosition.toDouble() / newHeight) * origH
        val cropW = (width.toDouble() / newWidth) * origW
        val cropH = (height.toDouble() / newHeight) * origH
        val canvas = document.createElement("canvas") as HTMLCanvasElement
        canvas.width = width
        canvas.height = height
        val ctx = canvas.getContext("2d")?.unsafeCast<dynamic>() ?: return null
        ctx.drawImage(img, startX, startY, cropW, cropH, 0.0, 0.0, width.toDouble(), height.toDouble())
        val outW = outputWidth ?: width
        val outH = outputHeight ?: height
        val finalCanvas = if (outW != width || outH != height) {
            val c2 = document.createElement("canvas") as HTMLCanvasElement
            c2.width = outW
            c2.height = outH
            val ctx2 = c2.getContext("2d")?.unsafeCast<dynamic>() ?: return null
            ctx2.drawImage(canvas, 0.0, 0.0, width.toDouble(), height.toDouble(), 0.0, 0.0, outW.toDouble(), outH.toDouble())
            c2
        } else {
            canvas
        }
        val outDataUrl = finalCanvas.toDataURL("image/png")
        SCImage(dataUrlToByteArray(outDataUrl))
    } catch (_: Throwable) {
        null
    }
}

private val tempImageStore = mutableMapOf<String, ByteArray>()

actual fun saveTempImgOnDisk(fileName: String, image: SCImage): Boolean {
    tempImageStore[fileName] = image.data.copyOf()
    return true
}

actual fun getTempImagePath(fileName: String): String? {
    val bytes = tempImageStore[fileName] ?: return null
    return bytes.toBase64DataUrl()
}

actual fun getTempImageBytes(fileName: String): ByteArray? {
    return tempImageStore[fileName]?.copyOf()
}
